import { Loader2 } from 'lucide-react';

import { LeadTimeAlert } from './LeadTimeAlert';
import { SpaceConflictPanel } from './SpaceConflictPanel';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { messages } from '@/shared/constants/messages';
import type { LeadTimeWarning } from '@/shared/constants/projectSizes';
import { toUserMessage } from '@/shared/lib/errors';
import type { ProjectDecision, SpaceConflict } from '@/shared/types/domain';

export const DECISION_TITLES: Record<ProjectDecision, string> = {
  approve: messages.review.actions.approve,
  request_changes: messages.review.actions.requestChanges,
  reject: messages.review.actions.reject,
};

const DECISION_HELP: Record<ProjectDecision, string | null> = {
  approve: null,
  request_changes: messages.review.commentRequiredOnChanges,
  reject: messages.review.commentRequiredOnReject,
};

/**
 * **A cor atravessa a confirmação** (decisão 41): o botão que fecha a decisão
 * usa a mesma cor do verbo que abriu o formulário — azul da marca em "Aprovar"
 * (é a ação da tela, ver `DecisionPanel`), a cor do estado nos outros dois.
 * Aqui não há mais escolha entre três — é a ação da tela —, então os três são
 * sólidos ou claros, nunca `outline` de repouso.
 *
 * ⚠️ **"Solicitar ajustes" é botão CLARO, e âmbar sólido está proibido.** Com
 * texto branco o âmbar não passa em contraste; escurecido o bastante para
 * passar, ele deixa de ser lido como âmbar e vira marrom. A saída é a mesma
 * tríade do crachá `warning` (`StatusBadge`), que é onde ela já provou que se
 * lê: fundo `amber-50`, borda `amber-300`, texto `amber-900`. O hover vai uma
 * parada mais escuro no fundo e na borda, sem trocar a cor do texto.
 */
const CONFIRM_VARIANT: Record<ProjectDecision, 'default' | 'destructive' | 'outline'> = {
  approve: 'default',
  request_changes: 'outline',
  reject: 'destructive',
};

const CONFIRM_CLASSES: Record<ProjectDecision, string | undefined> = {
  approve: undefined,
  request_changes:
    'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400 hover:bg-amber-100 hover:text-amber-900',
  reject: undefined,
};

interface DecisionFormProps {
  decision: ProjectDecision;
  comment: string;
  onChangeComment: (value: string) => void;
  conflicts: readonly SpaceConflict[];
  /** `null` quando o projeto está dentro do prazo do porte dele (US-21). */
  leadTime: LeadTimeWarning | null;
  isSubmitting: boolean;
  error: unknown;
  onBack: () => void;
  onConfirm: () => void;
}

/**
 * A coluna vira ISTO no lugar do bloco de ações (§6.4) — nunca um modal: o
 * revisor precisa reler a seção que motivou o apontamento enquanto escreve, e
 * um diálogo por cima do documento impediria exatamente isso.
 *
 * **A regra dos dois alertas aqui dentro** (decisão 36): a última tela antes do
 * irreversível repete tudo que desaconselha o irreversível. Conflito de espaço
 * já cumpria isso desde a US-22; o de porte passa a cumprir agora, porque na 3ª
 * rodada os dois saíram da coluna `lg:sticky` e foram para junto dos fatos no
 * documento — e dá para chegar em "Aprovar" sem nunca ter rolado até lá.
 */
export function DecisionForm({
  decision,
  comment,
  onChangeComment,
  conflicts,
  leadTime,
  isSubmitting,
  error,
  onBack,
  onConfirm,
}: DecisionFormProps) {
  // Comentário obrigatório em `request_changes`/`reject`, opcional em
  // `approve` — o mesmo mapa que `project_transitions` guarda no banco
  // (§4.3); aqui é só conveniência de UX, a RPC é quem recusa de verdade.
  const commentRequired = decision !== 'approve';
  const canConfirm = !commentRequired || comment.trim().length > 0;

  return (
    // Sem título próprio aqui: no desktop, `DecisionPanel` desenha um `<h2>`
    // por cima; no celular, a folha (`Sheet`) já mostra o verbo no cabeçalho
    // dela. Um terceiro título aqui duplicaria um dos dois.
    <div className="animate-rise-in space-y-4">
      {/* Só no ramo `approve`: aprovar é o único verbo que confirma o projeto
          contra o que os alertas dizem. Quem pede ajustes ou rejeita não está
          passando por cima de nada — repetir os alertas ali seria ruído. */}
      {decision === 'approve' ? (
        <>
          {/* US-22, desde a 1ª rodada. */}
          <SpaceConflictPanel conflicts={conflicts} isLoading={false} />
          {/* US-21, novo na 3ª rodada — sem isto, mover o alerta de porte para
              o documento enfraqueceria a US. */}
          {leadTime ? <LeadTimeAlert {...leadTime} /> : null}
        </>
      ) : null}

      <div className="space-y-1.5">
        <label htmlFor="decision-comment" className="text-sm font-medium">
          Observação {commentRequired ? null : `(${messages.form.optional})`}
        </label>
        <Textarea
          id="decision-comment"
          value={comment}
          onChange={(event) => onChangeComment(event.target.value)}
          rows={5}
          disabled={isSubmitting}
          autoFocus
        />
        {DECISION_HELP[decision] ? (
          <p className="text-xs text-muted-foreground">{DECISION_HELP[decision]}</p>
        ) : null}
      </div>

      {error ? (
        <p
          role="alert"
          className="animate-reveal rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {toUserMessage(error)}
        </p>
      ) : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {/* "Voltar" continua `outline` NEUTRO, sem cor nenhuma — é o que impede
            que ele se confunda com o botão claro de ajustes ao lado. */}
        <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
          {messages.actions.back}
        </Button>
        <Button
          type="button"
          variant={CONFIRM_VARIANT[decision]}
          className={CONFIRM_CLASSES[decision]}
          onClick={onConfirm}
          disabled={isSubmitting || !canConfirm}
        >
          {isSubmitting ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {isSubmitting ? messages.review.deciding : DECISION_TITLES[decision]}
        </Button>
      </div>
    </div>
  );
}
